<template>
  <ContentSection :spacing="index === 0 ? 'sm' : 'default'">
    <Container indent="wide">
      <!-- SEARCH INPUT -->
      <SearchInput
        :model-value="filter.text"
        :placeholder="$t('full_text_search_in_the_documents')"
        class="mb-8 w-full max-w-[51rem] sm:mb-10"
        auto-focus
        @update:model-value="
          filter = {
            text: $event,
            page: 0,
            hitsPerPage: hitsPerPage,
          }
        "
      />

      <!-- AMOUNT OF RESULTS -->
      <p ref="amountOfResults" class="mb-5 text-sm opacity-60">
        {{ t('search.documents_found', results.nbHits || 0) }}
      </p>

      <!-- RESULTS DESKTOP -->
      <table
        v-show="Array.isArray(results.hits) && results.hits.length > 0"
        class="-mx-4 hidden w-full border-spacing-1 lg:table"
      >
        <thead>
          <tr class="text-left align-top text-sm opacity-50">
            <th class="pb-4 pl-4 pr-16 font-normal">
              {{ $t('title') }}
            </th>

            <th class="pb-4 pr-16 font-normal">{{ $t('author') }}</th>
            <th class="pb-4 pr-16 font-normal">{{ $t('year') }}</th>
            <th class="pb-4 pr-16 font-normal">ISSN/ISBN</th>
            <th class="font-normal">{{ $t('language') }}</th>
            <th />
          </tr>
        </thead>

        <!-- DOCUMENTS -->
        <tbody>
          <LiteratureTableItem
            v-for="(document, i) in results.hits"
            :key="document.objectID"
            :index="i"
            :data="document"
          />
        </tbody>
      </table>
    </Container>
    <!-- RESULTS MOBILE -->
    <Container v-show="Array.isArray(results.hits) && results.hits.length > 0">
      <div class="grid gap-4 lg:hidden">
        <LiteratureCard
          v-for="document in results.hits"
          :key="document.objectID"
          :data="document"
        />
      </div>
    </Container>

    <Pagination
      :current-page="results.page"
      :total-pages="results.nbPages"
      @go-to-page="goToPage"
    />
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, default: null },
  index: { type: Number, default: null },
})

// VARIABLES
const route = useRoute()
const params = useUrlSearchParams('history')
const { t } = useI18n()

let results = ref({})
const hitsPerPage = 16

const amountOfResults = ref(null)

const algoliaIndexPrefix = useRuntimeConfig().public.ALGOLIA_INDEX_PREFIX
if (!algoliaIndexPrefix) {
  throw new Error('ALGOLIA_INDEX_PREFIX not set in runtime config')
}

// POPULATE THE FILTER
let routeQueryFilter = route.query[`literature-database-${props.data.id}`]
if (routeQueryFilter) {
  routeQueryFilter = JSON.parse(routeQueryFilter)
}

const filter = ref(
  routeQueryFilter || {
    input: '',
    page: 0,
    hitsPerPage: hitsPerPage,
  }
)

// WATCHERS
watch(
  filter,
  (newValue) => {
    params[`literature-database-${props.data.id}`] = JSON.stringify(newValue)
    searchDocuments()
  },
  { deep: true }
)

// FUNCTIONS
async function searchDocuments() {
  const { data } = await useAsyncAlgoliaSearch({
    indexName: `${algoliaIndexPrefix}literature_database`,
    query: filter.value.text,
    requestOptions: {
      page: filter.value.page,
      hitsPerPage: filter.value.hitsPerPage,
    },
  })

  results.value = data.value
}

function goToPage($event) {
  filter.value.page = $event
  scrollTo(amountOfResults.value)
}

function scrollTo(element) {
  if (!element) {
    return
  }

  const headerOffset = 300
  const elementPosition = element.offsetTop
  const offsetPosition = elementPosition - headerOffset

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  })
}

// INITIAL
// the initial fetch must be done in the setup function and can not be in an async function scope
const payload = await useAsyncAlgoliaSearch({
  indexName: `${algoliaIndexPrefix}literature_database`,
  query: filter.value.text,
  requestOptions: {
    page: filter.value.page,
    hitsPerPage: filter.value.hitsPerPage,
  },
})
results.value = payload?.data.value
</script>
